import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const SUBJECTS = ['Mathematics', 'English', 'Science', 'Social Studies', 'Computer'];

export default function TeacherGrades() {
  const [myClass, setMyClass] = useState(null);
  const [grades, setGrades] = useState([]);
  const [examName, setExamName] = useState('Mid Term 2026');
  const [subject, setSubject] = useState('Mathematics');
  const [totalMarks, setTotalMarks] = useState(100);
  const [marksMap, setMarksMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchClass(); }, []);
  useEffect(() => { if (myClass) fetchGrades(); }, [myClass, examName, subject]);

  const fetchClass = async () => {
    try {
      const res = await api.get('/teacher/my-class');
      setMyClass(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGrades = async () => {
    try {
      const res = await api.get(`/grades/?class_id=${myClass.class_id}`);
      const filtered = res.data.filter(g => g.exam_name === examName && g.subject === subject);
      setGrades(filtered);
      const map = {};
      filtered.forEach(g => {
        map[g.student_id] = g.marks_obtained;
        if (g.total_marks) setTotalMarks(g.total_marks);
      });
      setMarksMap(map);
    } catch (err) {
      console.error(err);
    }
  };

  const isInvalid = (marks) => {
    if (marks === '' || marks === null || marks === undefined) return false;
    const num = parseFloat(marks);
    return isNaN(num) || num < 0 || num > totalMarks;
  };

  const handleSaveAll = async () => {
    const records = Object.entries(marksMap).filter(([_, m]) => m !== '' && m !== null && m !== undefined);
    if (records.length === 0) {
      alert('Enter marks for at least one student');
      return;
    }

    // ===== FRONTEND VALIDATION =====
    const invalidRows = records.filter(([_, marks]) => isInvalid(marks));

    if (invalidRows.length > 0) {
      alert(
        `Some marks are invalid:\n\n` +
        invalidRows.map(([id, marks]) => {
          const s = myClass.students.find(x => x.student_id === parseInt(id));
          return `• ${s?.name}: ${marks} (must be 0-${totalMarks})`;
        }).join('\n')
      );
      return;
    }

    setSaving(true);
    let saved = 0;
    const errors = [];

    for (const [studentId, marks] of records) {
      const existing = grades.find(g => g.student_id === parseInt(studentId));
      try {
        if (existing) {
          await api.put(`/grades/${existing.id}`, {
            marks_obtained: parseFloat(marks),
            total_marks: parseFloat(totalMarks),
          });
        } else {
          await api.post('/grades/', {
            student_id: parseInt(studentId),
            class_id: myClass.class_id,
            subject: subject,
            exam_name: examName,
            marks_obtained: parseFloat(marks),
            total_marks: parseFloat(totalMarks),
            remarks: null,
          });
        }
        saved++;
      } catch (err) {
        const s = myClass.students.find(x => x.student_id === parseInt(studentId));
        errors.push(`${s?.name}: ${err.response?.data?.detail || err.message}`);
      }
    }

    setSaving(false);

    if (errors.length > 0) {
      alert(`✅ Saved ${saved}, ❌ ${errors.length} failed:\n\n${errors.join('\n')}`);
    } else {
      alert(`✅ Saved ${saved} grade(s)`);
    }
    fetchGrades();
  };

  const calculateGrade = (marks) => {
    if (marks === '' || marks === null || marks === undefined) return '—';
    const num = parseFloat(marks);
    if (isNaN(num) || totalMarks <= 0) return '—';
    const pct = (num / totalMarks) * 100;
    if (pct >= 90) return 'A+';
    if (pct >= 85) return 'A';
    if (pct >= 80) return 'A-';
    if (pct >= 75) return 'B+';
    if (pct >= 70) return 'B';
    if (pct >= 65) return 'B-';
    if (pct >= 60) return 'C';
    if (pct >= 50) return 'D';
    return 'F';
  };

  if (loading) return <div className="bg-white p-6 rounded shadow text-center">Loading...</div>;
  if (!myClass?.class_name) return <div className="bg-white p-6 rounded shadow text-center">No class assigned.</div>;

  const invalidCount = myClass.students.filter(
    s => isInvalid(marksMap[s.student_id] ?? '')
  ).length;
  const filledCount = Object.values(marksMap).filter(v => v !== '' && v !== null).length;

  return (
    <div>
      <div className="bg-white p-5 rounded shadow mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">📝 Enter Grades</h1>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Exam</label>
            <input
              className="p-2 border rounded"
              value={examName}
              onChange={e => setExamName(e.target.value)}
              placeholder="Exam name"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Subject</label>
            <select
              className="p-2 border rounded"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            >
              {SUBJECTS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Total Marks</label>
            <input
              type="number"
              min="1"
              className="p-2 border rounded w-24"
              value={totalMarks}
              onChange={e => setTotalMarks(parseInt(e.target.value) || 100)}
            />
          </div>
          <button
            onClick={handleSaveAll}
            disabled={saving || invalidCount > 0}
            className="ml-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : <><i className="fas fa-save mr-1"></i> Save All Grades</>}
          </button>
        </div>

        {/* Validation / progress info */}
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-gray-500">
            <i className="fas fa-users"></i> {filledCount} / {myClass.students.length} students filled
          </span>
          {invalidCount > 0 && (
            <span className="text-red-600 font-semibold">
              <i className="fas fa-exclamation-triangle"></i> {invalidCount} invalid mark{invalidCount > 1 ? 's' : ''} (must be 0-{totalMarks})
            </span>
          )}
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-lg font-semibold mb-3">
          👨‍🎓 {examName} · {subject}
          <span className="text-sm font-normal text-gray-500 ml-2">
            (out of {totalMarks})
          </span>
        </h2>
        <div className="space-y-2">
          {myClass.students.map(s => {
            const rawMarks = marksMap[s.student_id];
            const marks = rawMarks ?? '';
            const invalid = isInvalid(rawMarks);
            const letter = calculateGrade(marks);

            return (
              <div
                key={s.student_id}
                className={`flex items-center gap-4 p-3 rounded border ${
                  invalid ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                  {s.first_name[0]}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-gray-500">
                    {s.roll_no ? `Roll #${s.roll_no}` : 'Roll not assigned'} · Reg: {s.registration_number}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max={totalMarks}
                    placeholder="Marks"
                    className={`w-24 p-2 border rounded text-center font-semibold ${
                      invalid
                        ? 'border-red-500 bg-red-50 text-red-700 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    value={marks}
                    onChange={e => setMarksMap({ ...marksMap, [s.student_id]: e.target.value })}
                  />
                  {invalid && (
                    <p className="absolute -bottom-4 left-0 right-0 text-[10px] text-red-600 text-center whitespace-nowrap">
                      max {totalMarks}
                    </p>
                  )}
                </div>
                <span
                  className={`w-16 text-center text-lg font-bold ${
                    invalid ? 'text-red-400' : 'text-gray-700'
                  }`}
                >
                  {invalid ? '!' : letter}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}